// 💵 Gestión CxC / Gestión CxP — Cartera de facturas con abonos y saldos.
// CxC: facturas EMITIDAS procesadas (financiero.tickets) → cobros.
// CxP: facturas RECIBIDAS procesadas (financiero.tickets_buy) → pagos.
// Estados derivados del saldo y vencimiento: pendiente / parcial / vencida /
// cobrada·pagada. El vencimiento viene del plazo del XML y es editable.
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileText,
  Filter,
  HandCoins,
  Loader2,
  Plus,
  Trash2,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { SortableTh, useSort } from './tablaSort';
import { fechaLocal } from '@/lib/utils/fecha';
import { htmlEstadoCuenta } from './estadoCuentaPdf';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const CB = { headers: { 'Content-Profile': 'control_bancario', 'Accept-Profile': 'control_bancario' } };

interface CuentaBanco { id: string; alias: string | null; numero: string; moneda: string; banco?: { nombre: string } }

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const FORMAS_PAGO = ['transferencia', 'efectivo', 'cheque', 'tarjeta', 'otro'];

const usd = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

const urlRide = (rideUrl: string | null) =>
  rideUrl ? rideUrl.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null;

interface ModoCartera {
  titulo: string;
  descripcion: string;
  icono: LucideIcon;
  contraparte: string;            // Cliente / Proveedor
  estadoFinal: string;            // cobrada / pagada
  accion: string;                 // Registrar cobro / Registrar pago
  movimientos: string;            // Cobros / Pagos
  rpcListar: string;
  rpcAbonos: string;
  rpcRegistrar: string;
  rpcEliminar: string;
  rpcVencimiento: string;
  rpcAging: string;
}

const MODO_CXC: ModoCartera = {
  titulo: 'Cartera por Cobrar',
  descripcion: 'Cuentas por cobrar: facturas emitidas procesadas, cobros y saldos',
  icono: HandCoins,
  contraparte: 'Cliente',
  estadoFinal: 'cobrada',
  accion: 'Registrar cobro',
  movimientos: 'Cobros',
  rpcListar: 'cxc_listar',
  rpcAbonos: 'cxc_abonos_de',
  rpcRegistrar: 'cxc_registrar_abono',
  rpcEliminar: 'cxc_eliminar_abono',
  rpcVencimiento: 'cxc_actualizar_vencimiento',
  rpcAging: 'cxc_aging',
};

const MODO_CXP: ModoCartera = {
  titulo: 'Cartera por Pagar',
  descripcion: 'Cuentas por pagar: facturas recibidas procesadas, pagos y saldos',
  icono: CreditCard,
  contraparte: 'Proveedor',
  estadoFinal: 'pagada',
  accion: 'Registrar pago',
  movimientos: 'Pagos',
  rpcListar: 'cxp_listar',
  rpcAbonos: 'cxp_abonos_de',
  rpcRegistrar: 'cxp_registrar_abono',
  rpcEliminar: 'cxp_eliminar_abono',
  rpcVencimiento: 'cxp_actualizar_vencimiento',
  rpcAging: 'cxp_aging',
};

export function CxcFacturas() {
  return <CarteraFacturas cfg={MODO_CXC} />;
}

export function CxpFacturas() {
  return <CarteraFacturas cfg={MODO_CXP} />;
}

interface FacturaCartera {
  id: string;
  serie_comprobante: string | null;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  importe_total: number;
  partner: string | null;
  identificacion: string | null;
  proyecto_id: string | null;
  proyecto: string | null;
  ride_url: string | null;
  abonado: number;
  abono_anticipo: number;
  abono_nc: number;
  abono_retencion: number;
  saldo: number;
  estado: 'pendiente' | 'parcial' | 'vencida' | 'cobrada' | 'pagada';
  dias_vencida: number;
  estado_emision?: string | null;   // estado de emisión electrónica SRI (solo CxC)
}

interface Resumen {
  facturas: number;
  importe: number;
  abonado: number;
  saldo: number;
  vencido: number;
}

interface Abono {
  id: string;
  fecha: string;
  monto: number;
  forma_pago: string | null;
  referencia: string | null;
  observaciones: string | null;
  usuario: string | null;
}

interface AgingPartner {
  partner: string;
  identificacion: string | null;
  facturas: number;
  por_vencer: number;
  d30: number;
  d60: number;
  d90: number;
  d90mas: number;
  total: number;
}

interface AgingTotales {
  facturas: number;
  por_vencer: number;
  d30: number;
  d60: number;
  d90: number;
  d90mas: number;
  total: number;
}

const ESTADO_CHIP: Record<string, string> = {
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  parcial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  vencida: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  cobrada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  pagada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

// Estado de emisión electrónica ante el SRI (independiente del cobro). Etiqueta +
// color por cada valor de tickets.estado_emision. NULL/'' ⇒ sin insignia ('—').
const SRI_EMISION: Record<string, { label: string; chip: string }> = {
  autorizado: { label: 'Autorizado', chip: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  por_emitir: { label: 'Por emitir', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  enviado:    { label: 'En proceso', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  recibida:   { label: 'En proceso', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  detenido:   { label: 'Detenido',   chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300' },
  rechazado:  { label: 'Rechazado',  chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  devuelta:   { label: 'Devuelta',   chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error:      { label: 'Error',      chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  anulado:    { label: 'Anulado',    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300' },
};

const selectCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const inputCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';


function CarteraFacturas({ cfg }: { cfg: ModoCartera }) {
  const queryClient = useQueryClient();
  const anioActual = new Date().getFullYear();
  const Icono = cfg.icono;

  const esCxc = cfg.rpcListar === 'cxc_listar';
  const [vista, setVista] = useState<'facturas' | 'aging' | 'sinret' | 'conret'>('facturas');
  const [agruparAging, setAgruparAging] = useState<'partner' | 'proyecto' | 'centro'>('partner');
  const [filtros, setFiltros] = useState({ estado: 'pendientes', anio: '', mes: '', partner: '', proyecto: '', serie: '' });
  const [aplicados, setAplicados] = useState(filtros);
  const [facturaAbierta, setFacturaAbierta] = useState<FacturaCartera | null>(null);

  // Catálogo de proyectos (centros de costo) para el filtro
  const { data: proyectos = [] } = useQuery<Array<{ id: string; razon_social: string; identificacion: string }>>({
    queryKey: ['sriPartners'],
    queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
    staleTime: 60_000,
  });

  const { data, isFetching, refetch } = useQuery<{ documentos: FacturaCartera[]; resumen: Resumen }>({
    queryKey: ['cartera', cfg.rpcListar, aplicados],
    queryFn: async () => {
      const { data: res } = await apiClient.post(`/rpc/${cfg.rpcListar}`, {
        _estado: aplicados.estado,
        _anio: aplicados.anio ? Number(aplicados.anio) : null,
        _mes: aplicados.mes ? Number(aplicados.mes) : null,
        _partner: aplicados.partner || null,
        _proyecto: aplicados.proyecto || null,
        _serie: aplicados.serie || null,
      }, FIN);
      return res;
    },
  });

  const documentos = data?.documentos || [];
  const resumen = data?.resumen;

  const { data: aging, isFetching: cargandoAging } = useQuery<{ partners: AgingPartner[]; totales: AgingTotales }>({
    queryKey: ['carteraAging', cfg.rpcAging, aplicados.partner, agruparAging],
    queryFn: async () =>
      (await apiClient.post(`/rpc/${cfg.rpcAging}`,
        { _partner: aplicados.partner || null, _agrupar: agruparAging }, FIN)).data,
    enabled: vista === 'aging',
  });

  // Ordenamiento por columna en cada pestaña
  const facSort = useSort<FacturaCartera>(documentos);
  const agingSort = useSort<AgingPartner>(aging?.partners || []);

  // Exportar (CSV / XLS / PDF) la tabla de facturas — respeta filtros y orden actuales
  const nombreFacturas = `cartera-${esCxc ? 'cxc' : 'cxp'}-facturas-${fechaLocal()}`;
  const headersFacturas = ['N°', 'Emisión', 'Serie', cfg.contraparte, 'Identificación', 'Proyecto', 'Vencimiento', 'Importe', 'Abonado', 'Anticipo', 'Nota de Crédito', 'Retenciones', 'Saldo', 'Estado'];
  const filasFacturas: Celda[][] = [
    ...facSort.sorted.map((f, i): Celda[] => [
      i + 1, f.fecha_emision ?? '', f.serie_comprobante ?? '', f.partner ?? '', f.identificacion ?? '', f.proyecto ?? '', f.fecha_vencimiento ?? '',
      Number(f.importe_total || 0), Number(f.abonado || 0), Number(f.abono_anticipo || 0), Number(f.abono_nc || 0),
      Number(f.abono_retencion || 0), Number(f.saldo || 0), f.estado ?? '',
    ]),
    ['', '', '', '', '', '', 'TOTAL',
      facSort.sorted.reduce((a, f) => a + Number(f.importe_total || 0), 0),
      facSort.sorted.reduce((a, f) => a + Number(f.abonado || 0), 0),
      facSort.sorted.reduce((a, f) => a + Number(f.abono_anticipo || 0), 0),
      facSort.sorted.reduce((a, f) => a + Number(f.abono_nc || 0), 0),
      facSort.sorted.reduce((a, f) => a + Number(f.abono_retencion || 0), 0),
      facSort.sorted.reduce((a, f) => a + Number(f.saldo || 0), 0), ''],
  ];

  // Estado de cuenta (PDF imprimible) de la contraparte
  const [ecPartner, setEcPartner] = useState<{ ident: string; nombre: string } | null>(null);
  const verEstadoCuenta = async (identificacion: string, desde: string | null, hasta: string | null) => {
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite ventanas emergentes para ver el estado de cuenta'); return; }
    win.document.write('<p style="font-family:sans-serif;padding:2rem">Generando estado de cuenta…</p>');
    try {
      const { data } = await apiClient.post(`/rpc/${esCxc ? 'cxc' : 'cxp'}_estado_cuenta`,
        { _identificacion: identificacion, _desde: desde || null, _hasta: hasta || null }, FIN);
      win.document.open();
      win.document.write(htmlEstadoCuenta(data));
      win.document.close();
    } catch {
      win.document.body.innerHTML = '<p style="font-family:sans-serif;padding:2rem">No se pudo generar el estado de cuenta.</p>';
    }
  };

  const actualizarVencimiento = useMutation({
    mutationFn: async ({ ticketId, fecha }: { ticketId: string; fecha: string }) => {
      await apiClient.post(`/rpc/${cfg.rpcVencimiento}`, { _ticket_id: ticketId, _fecha: fecha }, FIN);
    },
    onSuccess: () => {
      toast.success('Vencimiento actualizado');
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo actualizar el vencimiento'),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Icono className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{cfg.titulo}</h1>
          <p className="text-sm text-muted-foreground">{cfg.descripcion}</p>
        </div>
      </div>

      {/* Vistas: Facturas / Antigüedad de saldos / (CxC) Sin retención / Con retención */}
      <div className="flex gap-1 border-b">
        {(([['facturas', 'Facturas'], ['aging', 'Antigüedad de saldos'],
            ...(esCxc ? [['sinret', 'Sin retención'], ['conret', 'Con retención']] : [])] as const) as ReadonlyArray<readonly ['facturas' | 'aging' | 'sinret' | 'conret', string]>).map(([clave, etiqueta]) => (
          <button
            key={clave}
            onClick={() => setVista(clave)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              vista === clave
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {vista === 'facturas' && (<>
      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Facturas</p>
            <p className="text-2xl font-bold mt-1">{resumen.facturas.toLocaleString(LOCALE)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center justify-between">Importe <Wallet className="w-4 h-4 text-primary" /></p>
            <p className="text-2xl font-bold mt-1">{usd(resumen.importe)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{cfg.movimientos} registrados</p>
            <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">{usd(resumen.abonado)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Saldo pendiente</p>
            <p className="text-2xl font-bold mt-1 text-yellow-600 dark:text-yellow-400">{usd(resumen.saldo)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center justify-between">Vencido <AlertCircle className="w-4 h-4 text-red-500" /></p>
            <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">{usd(resumen.vencido)}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-estado">Estado</label>
              <select id="ca-estado" className={selectCls} value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                <option value="todas">Todas</option>
                <option value="pendientes">Con saldo</option>
                <option value="vencidas">Vencidas</option>
                <option value="cobradas">{cfg.estadoFinal === 'cobrada' ? 'Cobradas' : 'Pagadas'}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-anio">Año</label>
              <select id="ca-anio" className={selectCls} value={filtros.anio}
                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}>
                <option value="">Todos</option>
                {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-mes">Mes</label>
              <select id="ca-mes" className={selectCls} value={filtros.mes}
                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}>
                <option value="">Todos</option>
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-serie">Serie</label>
              <input id="ca-serie" className={`${inputCls} w-40`} placeholder="001-101-000…"
                value={filtros.serie}
                onChange={(e) => setFiltros({ ...filtros, serie: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') setAplicados(filtros); }} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-partner">{cfg.contraparte}</label>
              <input id="ca-partner" className={`${inputCls} w-56`} placeholder="Razón social o RUC…"
                value={filtros.partner}
                onChange={(e) => setFiltros({ ...filtros, partner: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') setAplicados(filtros); }} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ca-proyecto">Proyecto</label>
              <select id="ca-proyecto" className={`${selectCls} max-w-56`} value={filtros.proyecto}
                onChange={(e) => setFiltros({ ...filtros, proyecto: e.target.value })}>
                <option value="">Todos</option>
                <option value="SIN">(Sin proyecto)</option>
                {proyectos.map((p) => (
                  <option key={p.id} value={p.id}>{p.razon_social}</option>
                ))}
              </select>
            </div>
            <Button size="sm" onClick={() => setAplicados(filtros)} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />}
              Filtrar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            La cartera se alimenta de las facturas clasificadas en Procesar
            {cfg.estadoFinal === 'cobrada' ? ' Emitidos' : ' Recibidos'}; lo no procesado aún no aparece aquí.
          </p>
        </CardContent>
      </Card>

      {/* Cartera */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Facturas ({documentos.length})</CardTitle>
            <ExportarBotones
              nombre={nombreFacturas} titulo={cfg.titulo} headers={headersFacturas} filas={filasFacturas}
              colsDerecha={[0, 7, 8, 9, 10, 11, 12]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${facSort.sorted.length} factura(s)`}
              fmtPDF={(v, c) => ([7, 8, 9, 10, 11, 12].includes(c) ? usd(Number(v)) : String(v ?? ''))}
              disabled={facSort.sorted.length === 0} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right w-12">N°</th>
                <SortableTh col="fecha_emision" label="Emisión" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="serie_comprobante" label="Serie" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="partner" label={cfg.contraparte} sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="identificacion" label="Identificación" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="fecha_vencimiento" label="Vencimiento" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="importe_total" label="Importe" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="abonado" label="Abonado" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="abono_anticipo" label="Anticipo" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="abono_nc" label="Nota de Crédito" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="abono_retencion" label="Retenciones" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="saldo" label="Saldo" align="right" sort={facSort.sort} onSort={facSort.toggle} />
                <SortableTh col="estado" label="Estado" sort={facSort.sort} onSort={facSort.toggle} />
                {esCxc && <SortableTh col="estado_emision" label="SRI" sort={facSort.sort} onSort={facSort.toggle} />}
                <th className="py-2 pr-3 text-center">PDF</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {facSort.sorted.length === 0 && (
                <tr><td colSpan={16} className="py-8 text-center text-muted-foreground">
                  Sin facturas para los filtros seleccionados
                </td></tr>
              )}
              {facSort.sorted.map((f, idx) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{idx + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_emision || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.serie_comprobante || '—'}</td>
                  <td className="py-2 pr-3">
                    <p className="truncate max-w-60" title={f.partner || ''}>{f.partner || '—'}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-60" title={f.proyecto || ''}>{f.proyecto || ''}</p>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{f.identificacion || '—'}</td>
                  <td className="py-2 pr-3">
                    <input
                      type="date"
                      className="h-8 rounded-md border border-input bg-background px-1.5 text-xs"
                      value={f.fecha_vencimiento || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          actualizarVencimiento.mutate({ ticketId: f.id, fecha: e.target.value });
                        }
                      }}
                      title="Fecha de vencimiento (editable)"
                    />
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(f.importe_total)}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">
                    {f.abonado > 0 ? usd(f.abonado) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-blue-600 dark:text-blue-400">
                    {f.abono_anticipo > 0 ? usd(f.abono_anticipo) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-amber-600 dark:text-amber-400">
                    {f.abono_nc > 0 ? usd(f.abono_nc) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-purple-600 dark:text-purple-400">
                    {f.abono_retencion > 0 ? usd(f.abono_retencion) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(f.saldo)}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CHIP[f.estado] || ''}`}>
                      {f.estado}{f.estado === 'vencida' && f.dias_vencida > 0 ? ` · ${f.dias_vencida}d` : ''}
                    </span>
                  </td>
                  {esCxc && (
                    <td className="py-2 pr-3">
                      {f.estado_emision && SRI_EMISION[f.estado_emision] ? (
                        <span className={`rounded-full px-2 py-0.5 text-xs ${SRI_EMISION[f.estado_emision].chip}`}>
                          {SRI_EMISION[f.estado_emision].label}
                        </span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  )}
                  <td className="py-2 pr-3 text-center">
                    {urlRide(f.ride_url) ? (
                      <a href={urlRide(f.ride_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary hover:bg-accent"
                        title="Ver RIDE (PDF)">
                        <FileText className="w-4 h-4" />
                      </a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant={f.saldo > 0 ? 'default' : 'outline'}
                      onClick={() => setFacturaAbierta(f)}>
                      {cfg.movimientos}
                    </Button>
                  </td>
                </tr>
              ))}
              {facSort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={6}>Totales ({facSort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(facSort.sorted.reduce((a, f) => a + Number(f.importe_total || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">{usd(facSort.sorted.reduce((a, f) => a + Number(f.abonado || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-blue-600 dark:text-blue-400">{usd(facSort.sorted.reduce((a, f) => a + Number(f.abono_anticipo || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-amber-600 dark:text-amber-400">{usd(facSort.sorted.reduce((a, f) => a + Number(f.abono_nc || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-purple-600 dark:text-purple-400">{usd(facSort.sorted.reduce((a, f) => a + Number(f.abono_retencion || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(facSort.sorted.reduce((a, f) => a + Number(f.saldo || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {/* Antigüedad de saldos (aging 30/60/90) por partner */}
      {vista === 'aging' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">
                Antigüedad de saldos por {agruparAging === 'proyecto' ? 'proyecto' : agruparAging === 'centro' ? 'centro de costo' : cfg.contraparte.toLowerCase()} · al {new Date().toLocaleDateString(LOCALE)}
              </CardTitle>
              <div className="flex items-end gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium" htmlFor="ag-agrupar">Agrupar por</label>
                  <select id="ag-agrupar" className={selectCls} value={agruparAging}
                    onChange={(e) => setAgruparAging(e.target.value as 'partner' | 'proyecto' | 'centro')}>
                    <option value="partner">{cfg.contraparte}</option>
                    <option value="proyecto">Proyecto</option>
                    <option value="centro">Centro de costo</option>
                  </select>
                </div>
                <input
                  className={`${inputCls} w-56`}
                  placeholder={`Filtrar ${cfg.contraparte.toLowerCase()}…`}
                  value={filtros.partner}
                  onChange={(e) => setFiltros({ ...filtros, partner: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') setAplicados(filtros); }}
                />
                <Button size="sm" onClick={() => setAplicados(filtros)} isLoading={cargandoAging}>
                  {!cargandoAging && <Filter className="w-4 h-4" />}
                  Filtrar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {!aging || aging.partners.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {cargandoAging ? 'Calculando…' : 'Sin saldos pendientes'}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <SortableTh col="partner" label={agruparAging === 'proyecto' ? 'Proyecto' : cfg.contraparte} sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="facturas" label="Facturas" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="por_vencer" label="Por vencer" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="d30" label="1–30 días" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="d60" label="31–60 días" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="d90" label="61–90 días" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="d90mas" label="+90 días" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <SortableTh col="total" label="Total saldo" align="right" sort={agingSort.sort} onSort={agingSort.toggle} />
                    <th className="py-2 text-center">Estado de cuenta</th>
                  </tr>
                </thead>
                <tbody>
                  {agingSort.sorted.map((p) => (
                    <tr key={`${p.partner}-${p.identificacion}`} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3">
                        <p className="truncate max-w-64" title={p.partner}>{p.partner}</p>
                        <p className="text-xs text-muted-foreground">{p.identificacion || ''}</p>
                      </td>
                      <td className="py-2 pr-3 text-right">{p.facturas}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{p.por_vencer > 0 ? usd(p.por_vencer) : '—'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-yellow-700 dark:text-yellow-400">
                        {p.d30 > 0 ? usd(p.d30) : '—'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-orange-600 dark:text-orange-400">
                        {p.d60 > 0 ? usd(p.d60) : '—'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-red-600 dark:text-red-400">
                        {p.d90 > 0 ? usd(p.d90) : '—'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap font-medium text-red-700 dark:text-red-300">
                        {p.d90mas > 0 ? usd(p.d90mas) : '—'}</td>
                      <td className="py-2 text-right whitespace-nowrap font-semibold">{usd(p.total)}</td>
                      <td className="py-2 text-center">
                        {agruparAging === 'partner' && p.identificacion ? (
                          <Button size="sm" variant="outline" onClick={() => setEcPartner({ ident: p.identificacion!, nombre: p.partner })}>
                            <FileText className="w-4 h-4 mr-1" /> Estado de cuenta
                          </Button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 pr-3">Total ({aging.totales.facturas} facturas)</td>
                    <td className="py-2 pr-3"></td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(aging.totales.por_vencer)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(aging.totales.d30)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(aging.totales.d60)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(aging.totales.d90)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(aging.totales.d90mas)}</td>
                    <td className="py-2 text-right whitespace-nowrap">{usd(aging.totales.total)}</td>
                    <td className="py-2"></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Facturas EMITIDAS sin / con retención — solo con saldo pendiente (solo CxC) */}
      {esCxc && vista === 'sinret' && <PanelFacturasRet cfg={cfg} conRetencion={false} />}
      {esCxc && vista === 'conret' && <PanelFacturasRet cfg={cfg} conRetencion />}

      {facturaAbierta && (
        <PanelAbonos
          cfg={cfg}
          factura={facturaAbierta}
          onClose={() => setFacturaAbierta(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['cartera'] });
          }}
        />
      )}

      {ecPartner && (
        <EstadoCuentaModal
          partner={ecPartner}
          anioActual={anioActual}
          onClose={() => setEcPartner(null)}
          onGenerar={(desde, hasta) => { void verEstadoCuenta(ecPartner.ident, desde, hasta); setEcPartner(null); }}
        />
      )}
    </div>
  );
}

// Modal para elegir el rango (fecha inicial / fecha de corte) del estado de cuenta
function EstadoCuentaModal({ partner, onClose, onGenerar }: {
  partner: { ident: string; nombre: string };
  anioActual: number;
  onClose: () => void;
  onGenerar: (desde: string | null, hasta: string | null) => void;
}) {
  const [desde, setDesde] = useState<string>('');
  const [hasta, setHasta] = useState<string>(fechaLocal());
  return (
    <PanelIntegrado title="Estado de cuenta" onClose={onClose}>
      <div className="space-y-3">
          <p className="text-sm text-muted-foreground truncate" title={partner.nombre}>{partner.nombre}</p>
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ec-desde">Fecha inicial</label>
              <input id="ec-desde" type="date" className={selectCls} value={desde} max={hasta || undefined}
                onChange={(e) => setDesde(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="ec-hasta">Fecha de corte</label>
              <input id="ec-hasta" type="date" className={selectCls} value={hasta} min={desde || undefined}
                onChange={(e) => setHasta(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {desde
              ? 'Arranca con el saldo anterior (todo lo previo a la fecha inicial) y muestra los movimientos entre la fecha inicial y la de corte.'
              : 'Sin fecha inicial: acumulado desde el principio hasta la fecha de corte (déjala vacía para llegar hasta hoy).'}
          </p>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => onGenerar(desde || null, hasta || null)}>
              Generar PDF
            </Button>
          </div>
      </div>
    </PanelIntegrado>
  );
}

// ---------------------------------------------------------------------------
// Panel de cobros/pagos de una factura
// ---------------------------------------------------------------------------
function PanelAbonos({
  cfg, factura, onClose, onChanged,
}: {
  cfg: ModoCartera;
  factura: FacturaCartera;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { data: abonos = [], refetch } = useQuery<Abono[]>({
    queryKey: ['carteraAbonos', cfg.rpcAbonos, factura.id],
    queryFn: async () =>
      (await apiClient.post(`/rpc/${cfg.rpcAbonos}`, { _ticket_id: factura.id }, FIN)).data || [],
  });

  const totalAbonado = abonos.reduce((s, a) => s + Number(a.monto), 0);
  const saldoActual = Math.round((Number(factura.importe_total) - totalAbonado) * 100) / 100;

  const esCxp = cfg.rpcRegistrar === 'cxp_registrar_abono';
  const [pagarBanco, setPagarBanco] = useState(false);
  const [cobrarBanco, setCobrarBanco] = useState(false);
  const [nuevo, setNuevo] = useState({
    fecha: fechaLocal(),
    monto: '',
    forma_pago: 'transferencia',
    referencia: '',
    observaciones: '',
  });

  const registrar = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/rpc/${cfg.rpcRegistrar}`, {
        _ticket_id: factura.id,
        _monto: Number(nuevo.monto),
        _fecha: nuevo.fecha,
        _forma_pago: nuevo.forma_pago || null,
        _referencia: nuevo.referencia || null,
        _observaciones: nuevo.observaciones || null,
      }, FIN);
      return data;
    },
    onSuccess: (res: any) => {
      toast.success(`${res?.message || 'Registrado'} · saldo ${usd(res?.saldo ?? 0)}`);
      // Un abono sin cuenta bancaria SÍ contabiliza, pero contra una cuenta
      // puente: el banco no se entera todavía. Decirlo aquí es la diferencia
      // entre una cuenta de paso y una cuenta olvidada.
      if (res?.sin_banco && res?.aviso) toast.warning(res.aviso, { duration: 7000 });
      setNuevo((n) => ({ ...n, monto: '', referencia: '', observaciones: '' }));
      void refetch();
      onChanged();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo registrar'),
  });

  const eliminar = useMutation({
    mutationFn: async (abonoId: string) => {
      await apiClient.post(`/rpc/${cfg.rpcEliminar}`, { _abono_id: abonoId }, FIN);
    },
    onSuccess: () => {
      toast.success('Movimiento eliminado');
      void refetch();
      onChanged();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo eliminar'),
  });

  const dlg = useDialogoEnLinea();

  return (
    <PanelIntegrado title={`${cfg.movimientos} · Factura ${factura.serie_comprobante}`} onClose={onClose} wide>
      {dlg.ui}
      <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">
              {factura.partner} · emitida {factura.fecha_emision} · vence {factura.fecha_vencimiento || '—'}
            </p>
            <p className="text-sm mt-1">
              Importe <span className="font-semibold">{usd(factura.importe_total)}</span> ·
              Abonado <span className="font-semibold text-green-600 dark:text-green-400"> {usd(totalAbonado)}</span> ·
              Saldo <span className="font-semibold text-yellow-600 dark:text-yellow-400"> {usd(saldoActual)}</span>
            </p>
          </div>

          {/* Nuevo abono */}
          {saldoActual > 0 ? (
            <div className="rounded-lg border p-4 bg-muted/20 space-y-3">
              <p className="text-sm font-semibold">{cfg.accion}</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium" htmlFor="ab-fecha">Fecha</label>
                  <input id="ab-fecha" type="date" className={inputCls} value={nuevo.fecha}
                    max={fechaLocal()}
                    onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium" htmlFor="ab-monto">Monto *</label>
                  <input id="ab-monto" type="number" min="0.01" step="0.01" max={saldoActual}
                    className={`${inputCls} w-28 text-right`} placeholder="0.00" value={nuevo.monto}
                    onChange={(e) => setNuevo({ ...nuevo, monto: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium" htmlFor="ab-forma">Forma de pago</label>
                  <select id="ab-forma" className={selectCls} value={nuevo.forma_pago}
                    onChange={(e) => setNuevo({ ...nuevo, forma_pago: e.target.value })}>
                    {FORMAS_PAGO.map((fp) => <option key={fp} value={fp}>{fp}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium" htmlFor="ab-ref">Referencia</label>
                  <input id="ab-ref" className={`${inputCls} w-36`} placeholder="Nº transferencia…"
                    value={nuevo.referencia}
                    onChange={(e) => setNuevo({ ...nuevo, referencia: e.target.value })} />
                </div>
                <div className="flex-1 min-w-40 space-y-1">
                  <label className="text-xs font-medium" htmlFor="ab-obs">Observaciones</label>
                  <input id="ab-obs" className={`${inputCls} w-full`} placeholder="Opcional"
                    value={nuevo.observaciones}
                    onChange={(e) => setNuevo({ ...nuevo, observaciones: e.target.value })} />
                </div>
                <Button size="sm" isLoading={registrar.isPending} onClick={() => {
                  const monto = Number(nuevo.monto);
                  if (!monto || monto <= 0) { toast.error('Ingrese un monto válido'); return; }
                  if (monto > saldoActual + 0.005) { toast.error(`El monto supera el saldo (${usd(saldoActual)})`); return; }
                  registrar.mutate();
                }}>
                  {!registrar.isPending && <Plus className="w-4 h-4" />}
                  Grabar
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2 pt-1 border-t">
                <p className="text-xs text-muted-foreground">
                  Atajo: monto exacto del saldo ({usd(saldoActual)}) deja la factura {cfg.estadoFinal}.
                </p>
                {esCxp ? (
                  <Button size="sm" variant="outline" onClick={() => setPagarBanco(true)}>
                    <CreditCard className="w-4 h-4 mr-1" /> Pagar por banco
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setCobrarBanco(true)}>
                    <CreditCard className="w-4 h-4 mr-1" /> Cobrar por banco
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Este registro contabiliza contra una <b>cuenta puente</b>: baja el saldo de la
                factura y el mayor, pero el banco no lo ve. Para que el movimiento llegue a la
                cuenta bancaria —y pase por el control de aprobación por monto— usa{' '}
                <b>{esCxp ? 'Pagar por banco' : 'Cobrar por banco'}</b>.
              </p>
            </div>
          ) : (
            <p className="rounded-lg border p-3 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20">
              Factura {cfg.estadoFinal}: sin saldo pendiente.
            </p>
          )}

          {/* Historial */}
          <div>
            <p className="text-sm font-semibold mb-2">Historial ({abonos.length})</p>
            {abonos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin movimientos registrados</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1.5 pr-3">Fecha</th>
                    <th className="py-1.5 pr-3 text-right">Monto</th>
                    <th className="py-1.5 pr-3">Forma</th>
                    <th className="py-1.5 pr-3">Referencia</th>
                    <th className="py-1.5 pr-3">Usuario</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {abonos.map((a) => (
                    <tr key={a.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 whitespace-nowrap">{a.fecha}</td>
                      <td className="py-1.5 pr-3 text-right whitespace-nowrap font-medium">{usd(a.monto)}</td>
                      <td className="py-1.5 pr-3">{a.forma_pago || '—'}</td>
                      <td className="py-1.5 pr-3">
                        <span title={a.observaciones || ''}>{a.referencia || '—'}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{a.usuario || '—'}</td>
                      <td className="py-1.5 text-right">
                        <button
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Eliminar movimiento"
                          onClick={async () => {
                            if (await dlg.confirmar(`¿Eliminar el movimiento de ${usd(a.monto)} del ${a.fecha}?`, { peligro: true })) {
                              eliminar.mutate(a.id);
                            }
                          }}
                        >
                          {eliminar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

      {cobrarBanco && (
        <CobrarBancoModal
          ticketId={factura.id}
          serie={factura.serie_comprobante}
          cliente={factura.partner}
          saldo={saldoActual}
          onClose={() => setCobrarBanco(false)}
          onDone={() => { setCobrarBanco(false); void refetch(); onChanged(); }}
        />
      )}

      {pagarBanco && (
        <PagarBancoModal
          ticketId={factura.id}
          serie={factura.serie_comprobante}
          proveedor={factura.partner}
          saldo={saldoActual}
          onClose={() => setPagarBanco(false)}
          onDone={() => { setPagarBanco(false); void refetch(); onChanged(); }}
        />
      )}
      </div>
    </PanelIntegrado>
  );
}


// ---------------------------------------------------------------------------
// Cobrar por banco: el espejo de «Pagar por banco». Una sola llamada registra
// el ingreso en el libro banco Y abona la factura — antes había que teclear el
// cobro dos veces (aquí y en Control Bancario) y, si se hacía una sola, o la
// cartera o el banco quedaba mintiendo. El asiento Banco/Clientes nace al
// conciliar el movimiento, igual que en cualquier otro movimiento del módulo.
// ---------------------------------------------------------------------------
function CobrarBancoModal({ ticketId, serie, cliente, saldo, onClose, onDone }: {
  ticketId: string; serie: string | null; cliente: string | null; saldo: number;
  onClose: () => void; onDone: () => void;
}) {
  const { data: cuentas = [] } = useQuery<CuentaBanco[]>({
    queryKey: ['cb-cuentas-para-cxc'],
    queryFn: async () => (await apiClient.get<CuentaBanco[]>(
      '/rest/control_bancario.cuentas?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero')).data || [],
  });
  const [form, setForm] = useState({
    cuenta_id: '', forma_pago: 'transferencia', fecha: fechaLocal(), monto: String(saldo),
  });

  const cobrar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/cb_cobrar_factura_cxc', {
      _ticket_id: ticketId,
      _cuenta_id: form.cuenta_id,
      _forma_pago: form.forma_pago,
      _fecha: form.fecha,
      _monto: Number(form.monto),
    }, CB)).data,
    onSuccess: (r: any) => {
      toast.success(`Cobro registrado en banco y en cartera · saldo ${usd(r?.saldo ?? 0)}`);
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo registrar el cobro'),
  });

  return (
    <PanelIntegrado title="Cobrar por banco" onClose={onClose}>
      <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {cliente} · Factura {serie} · saldo <span className="font-semibold">{usd(saldo)}</span>
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="cb-cuenta">Cuenta bancaria donde entró el dinero *</label>
            <select id="cb-cuenta" className={`${selectCls} w-full`} value={form.cuenta_id}
              onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })}>
              <option value="">— Selecciona —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="cb-forma">Forma</label>
              <select id="cb-forma" className={`${selectCls} w-full`} value={form.forma_pago}
                onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}>
                {FORMAS_PAGO.map((fp) => <option key={fp} value={fp}>{fp}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="cb-fecha">Fecha</label>
              <input id="cb-fecha" type="date" className={`${inputCls} w-full`} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="cb-monto">Monto</label>
              <input id="cb-monto" type="number" min="0.01" step="0.01" max={saldo}
                className={`${inputCls} w-full text-right`} value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Registra el ingreso en el libro banco <b>y</b> abona la factura, en una sola operación.
            El asiento contable nace al conciliar el movimiento.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button isLoading={cobrar.isPending}
              disabled={!form.cuenta_id || !(Number(form.monto) > 0)}
              onClick={() => cobrar.mutate()}>Registrar cobro</Button>
          </div>
      </div>
    </PanelIntegrado>
  );
}

// ---------------------------------------------------------------------------
// Pagar por banco: crea una Orden de Pago en Control Bancario ENLAZADA a la
// factura CxP. Al aprobarla y pagarla en Tesorería, el saldo de la factura se
// rebaja automáticamente (abono con forma de pago real).
// ---------------------------------------------------------------------------
function PagarBancoModal({ ticketId, serie, proveedor, saldo, onClose, onDone }: {
  ticketId: string; serie: string | null; proveedor: string | null; saldo: number;
  onClose: () => void; onDone: () => void;
}) {
  const { data: cuentas = [] } = useQuery<CuentaBanco[]>({
    queryKey: ['cb-cuentas-para-cxp'],
    queryFn: async () => (await apiClient.get<CuentaBanco[]>(
      '/rest/control_bancario.cuentas?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero')).data || [],
  });
  const [form, setForm] = useState({
    cuenta_id: '', forma_pago: 'transferencia', fecha: fechaLocal(), monto: String(saldo),
  });

  const crear = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/cb_orden_pago_crear', {
      _beneficiario: proveedor || 'Proveedor',
      _monto: Number(form.monto),
      _cuenta_id: form.cuenta_id,
      _forma_pago: form.forma_pago,
      _fecha: form.fecha,
      _concepto: `Pago factura ${serie ?? ''}`.trim(),
      _documento_ref: serie,
      _ticket_buy_id: ticketId,
    }, CB)).data,
    onSuccess: (r: any) => {
      toast.success(`Orden ${r?.numero ?? ''} creada en Control Bancario. Apruébala y págala en Tesorería para rebajar el saldo.`);
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo crear la orden de pago'),
  });

  const pagarDirecto = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/cb_pagar_factura_cxp', {
      _ticket_id: ticketId,
      _cuenta_id: form.cuenta_id,
      _forma_pago: form.forma_pago,
      _fecha: form.fecha,
      _monto: Number(form.monto),
    }, CB)).data,
    onSuccess: (r: any) => {
      toast.success(`Pago registrado · orden ${r?.numero ?? ''} · saldo ${usd(r?.saldo ?? 0)}`);
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo registrar el pago'),
  });
  const ocupado = crear.isPending || pagarDirecto.isPending;

  return (
    <PanelIntegrado title="Pagar por banco" onClose={onClose}>
      <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {proveedor} · Factura {serie} · saldo <span className="font-semibold">{usd(saldo)}</span>
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="pb-cuenta">Cuenta bancaria *</label>
            <select id="pb-cuenta" className={`${selectCls} w-full`} value={form.cuenta_id}
              onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })}>
              <option value="">— Selecciona —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pb-forma">Forma</label>
              <select id="pb-forma" className={`${selectCls} w-full`} value={form.forma_pago}
                onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}>
                {FORMAS_PAGO.map((fp) => <option key={fp} value={fp}>{fp}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pb-fecha">Fecha</label>
              <input id="pb-fecha" type="date" className={`${inputCls} w-full`} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="pb-monto">Monto</label>
              <input id="pb-monto" type="number" min="0.01" step="0.01" max={saldo}
                className={`${inputCls} w-full text-right`} value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            <b>Pagar ahora</b>: registra el pago de inmediato (débito en banco + abono en la factura), sin aprobación.
            <br /><b>Crear orden de pago</b>: la deja pendiente de aprobar/pagar en Tesorería (flujo con control).
          </p>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose} disabled={ocupado}>Cancelar</Button>
            <Button variant="outline" isLoading={crear.isPending} disabled={ocupado || !form.cuenta_id || Number(form.monto) <= 0 || Number(form.monto) > saldo + 0.005}
              onClick={() => crear.mutate()}>Crear orden de pago</Button>
            <Button isLoading={pagarDirecto.isPending} disabled={ocupado || !form.cuenta_id || Number(form.monto) <= 0 || Number(form.monto) > saldo + 0.005}
              onClick={() => pagarDirecto.mutate()}>Pagar ahora</Button>
          </div>
      </div>
    </PanelIntegrado>
  );
}

// ── Pestañas "Sin retención" / "Con retención" (solo CxC) ──────────────────────
// Facturas emitidas con saldo pendiente. Filtros: fechas + Partner/Proyecto/Centro
// de costo (client-side sobre lo traído). Tabla agrupada por Partner (contraparte)
// con expandir/colapsar y subtotales por grupo + total general.
interface RetRow {
  id: string; serie_comprobante: string | null; fecha_emision: string | null;
  importe_total: number; partner: string | null; identificacion: string | null;
  proyecto: string | null; centro: string | null; ride_url: string | null;
  abonado: number; retencion?: number; saldo: number;
}
interface RetResp {
  documentos: RetRow[];
  resumen: { facturas: number; importe: number; retencion?: number; saldo: number };
}

function PanelFacturasRet({ cfg, conRetencion }: { cfg: ModoCartera; conRetencion: boolean }) {
  const anio = new Date().getFullYear();
  const hoyStr = fechaLocal();
  const inicioAnioStr = `${anio}-01-01`;
  const [filtro, setFiltro] = useState({ desde: inicioAnioStr, hasta: hoyStr });
  const [aplicado, setAplicado] = useState(filtro);
  const [dim, setDim] = useState({ partner: '', proyecto: '', centro: '' });
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());

  const rpcFn = conRetencion ? 'cxc_con_retencion' : 'cxc_sin_retencion';
  const { data, isFetching } = useQuery<RetResp>({
    queryKey: ['carteraRet', rpcFn, aplicado],
    queryFn: async () => (await apiClient.post(`/rpc/${rpcFn}`,
      { _desde: aplicado.desde || null, _hasta: aplicado.hasta || null }, FIN)).data,
  });

  const docs = data?.documentos || [];
  const opciones = (key: 'partner' | 'proyecto' | 'centro') =>
    Array.from(new Set(docs.map((d) => d[key]).filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b));

  const filtrados = docs.filter((d) =>
    (!dim.partner || d.partner === dim.partner) &&
    (!dim.proyecto || d.proyecto === dim.proyecto) &&
    (!dim.centro || d.centro === dim.centro));

  const sort = useSort<RetRow>(filtrados);
  const rows = sort.sorted;

  // Agrupar por partner (contraparte), preservando el orden ya ordenado
  const grupos: Array<{ key: string; partner: string; rows: RetRow[] }> = [];
  const idxGrupo = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.partner || '(sin partner)'}|${r.identificacion || ''}`;
    let g = idxGrupo.get(key);
    if (g === undefined) { g = grupos.length; idxGrupo.set(key, g); grupos.push({ key, partner: r.partner || '(sin partner)', rows: [] }); }
    grupos[g].rows.push(r);
  }
  const sub = (rs: RetRow[]) => ({
    facturas: rs.length,
    importe: rs.reduce((a, r) => a + Number(r.importe_total || 0), 0),
    abonado: rs.reduce((a, r) => a + Number(r.abonado || 0), 0),
    retencion: rs.reduce((a, r) => a + Number(r.retencion || 0), 0),
    saldo: rs.reduce((a, r) => a + Number(r.saldo || 0), 0),
  });
  const tot = sub(rows);
  const toggleGrupo = (k: string) => setColapsados((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  // N° de registro sobre el orden visible (grupos expandidos)
  const visibles = grupos.flatMap((g) => (colapsados.has(g.key) ? [] : g.rows));
  const numero = new Map(visibles.map((r, i) => [r.id, i + 1]));

  const colsTexto = 5; // N°, Emisión, Serie, Proyecto, Centro

  // Exportar (plano): incluye Partner + Identificación como columnas
  const headers = ['N°', 'Emisión', 'Serie', cfg.contraparte, 'Identificación', 'Proyecto', 'Centro de costo',
    'Importe', 'Abonado', ...(conRetencion ? ['Retención'] : []), 'Saldo'];
  const filasExport: Celda[][] = [
    ...rows.map((r, i): Celda[] => [i + 1, r.fecha_emision ?? '', r.serie_comprobante ?? '', r.partner ?? '',
      r.identificacion ?? '', r.proyecto ?? '', r.centro ?? '', Number(r.importe_total || 0), Number(r.abonado || 0),
      ...(conRetencion ? [Number(r.retencion || 0)] : []), Number(r.saldo || 0)]),
    ['', '', '', '', '', '', 'TOTAL', tot.importe, tot.abonado, ...(conRetencion ? [tot.retencion] : []), tot.saldo],
  ];
  const idxMoney = conRetencion ? [7, 8, 9, 10] : [7, 8, 9];
  const idxDerecha = [0, ...idxMoney];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              Facturas emitidas {conRetencion ? 'con' : 'sin'} retención aplicada · con saldo
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {data
                ? `${tot.facturas} de ${data.resumen.facturas} factura(s) · importe ${usd(tot.importe)}${conRetencion ? ` · retención ${usd(tot.retencion)}` : ''} · saldo ${usd(tot.saldo)}`
                : 'Cargando…'}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Desde</label>
              <input type="date" className={inputCls} value={filtro.desde} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Hasta</label>
              <input type="date" className={inputCls} value={filtro.hasta} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value })} />
            </div>
            <Button size="sm" onClick={() => setAplicado(filtro)} isLoading={isFetching}>
              {!isFetching && <Filter className="w-4 h-4" />} Filtrar
            </Button>
            <ExportarBotones nombre={`facturas-${conRetencion ? 'con' : 'sin'}-retencion-${hoyStr}`}
              titulo={`Facturas ${conRetencion ? 'con' : 'sin'} retención (con saldo)`}
              headers={headers} filas={filasExport} colsDerecha={idxDerecha}
              fmtPDF={(v, c) => (idxMoney.includes(c) ? usd(Number(v)) : String(v ?? ''))}
              disabled={rows.length === 0} />
          </div>
        </div>
        {/* Filtros por dimensión (Partner / Proyecto / Centro de costo) */}
        <div className="flex flex-wrap items-end gap-2 mt-2">
          <div className="space-y-1">
            <label className="text-xs font-medium">{cfg.contraparte}</label>
            <select className={`${selectCls} max-w-56`} value={dim.partner} onChange={(e) => setDim({ ...dim, partner: e.target.value })}>
              <option value="">Todos</option>
              {opciones('partner').map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Proyecto</label>
            <select className={`${selectCls} max-w-56`} value={dim.proyecto} onChange={(e) => setDim({ ...dim, proyecto: e.target.value })}>
              <option value="">Todos</option>
              {opciones('proyecto').map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Centro de costo</label>
            <select className={`${selectCls} max-w-56`} value={dim.centro} onChange={(e) => setDim({ ...dim, centro: e.target.value })}>
              <option value="">Todos</option>
              {opciones('centro').map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          {(dim.partner || dim.proyecto || dim.centro) && (
            <Button size="sm" variant="outline" onClick={() => setDim({ partner: '', proyecto: '', centro: '' })}>Limpiar</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {isFetching ? 'Calculando…' : 'No hay facturas con saldo pendiente en el rango/filtros'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 w-10">N°</th>
                <SortableTh col="fecha_emision" label="Emisión" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh col="serie_comprobante" label="Serie" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh col="proyecto" label="Proyecto" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh col="centro" label="Centro de costo" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh col="importe_total" label="Importe" align="right" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh col="abonado" label="Abonado" align="right" sort={sort.sort} onSort={sort.toggle} />
                {conRetencion && <SortableTh col="retencion" label="Retención" align="right" sort={sort.sort} onSort={sort.toggle} />}
                <SortableTh col="saldo" label="Saldo" align="right" sort={sort.sort} onSort={sort.toggle} />
                <th className="py-2 pr-3 text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => {
                const s = sub(g.rows);
                const abierto = !colapsados.has(g.key);
                return (
                  <Fragment key={g.key}>
                    <tr className="bg-muted/50 border-b cursor-pointer select-none" onClick={() => toggleGrupo(g.key)}>
                      <td className="py-2 pr-3 font-medium" colSpan={colsTexto}>
                        <span className="inline-flex items-center gap-1">
                          {abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="truncate max-w-96" title={g.partner}>{g.partner}</span>
                          <span className="text-xs text-muted-foreground">({s.facturas})</span>
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(s.importe)}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(s.abonado)}</td>
                      {conRetencion && <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(s.retencion)}</td>}
                      <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(s.saldo)}</td>
                      <td className="py-2"></td>
                    </tr>
                    {abierto && g.rows.map((r) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-muted-foreground">{numero.get(r.id)}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{r.fecha_emision || '—'}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{r.serie_comprobante || '—'}</td>
                        <td className="py-2 pr-3">
                          {r.proyecto ? <span className="truncate max-w-44 inline-block align-middle" title={r.proyecto}>{r.proyecto}</span> : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2 pr-3">
                          {r.centro ? <span className="truncate max-w-44 inline-block align-middle" title={r.centro}>{r.centro}</span> : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(r.importe_total)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">{r.abonado > 0 ? usd(r.abonado) : '—'}</td>
                        {conRetencion && <td className="py-2 pr-3 text-right whitespace-nowrap text-purple-600 dark:text-purple-400">{usd(r.retencion ?? 0)}</td>}
                        <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(r.saldo)}</td>
                        <td className="py-2 pr-3 text-center">
                          {urlRide(r.ride_url) ? (
                            <a href={urlRide(r.ride_url)!} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary hover:bg-accent" title="Ver RIDE (PDF)">
                              <FileText className="w-4 h-4" />
                            </a>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold border-t-2">
                <td className="py-2 pr-3" colSpan={colsTexto}>Total general ({tot.facturas} facturas)</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(tot.importe)}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(tot.abonado)}</td>
                {conRetencion && <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(tot.retencion)}</td>}
                <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(tot.saldo)}</td>
                <td className="py-2"></td>
              </tr>
            </tfoot>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
